import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { useNotify } from './notify';
import { ApiError, NetworkError } from './problem';

/**
 * The one way a screen writes.
 *
 * Every master-data screen needs the same four things around a write — send it, invalidate what it
 * changed, confirm it, and report a failure in the server's own words — and writing that out per
 * button is how one screen ends up swallowing errors while its neighbour shows them twice.
 *
 * Both outcomes are reported through {@link useNotify}, so a write that succeeded on one screen
 * looks exactly like a write that succeeded on another, and both land in the same corner. The
 * failure path hands over the raw error rather than a message: the server's `detail` is already
 * localized and already ordered most-fixable-first, and the tone has to be derived from the
 * refusal kind so a plan limit is not painted as a fault.
 */
export function useApiMutation<TResult, TInput = void>(options: {
  mutationFn: (input: TInput) => Promise<TResult>;
  /** Shown on success. Omit for writes whose result is visible on screen anyway. */
  success?: string | ((result: TResult, input: TInput) => string) | undefined;
  /** Query keys whose data this write invalidates. */
  invalidates?: readonly (readonly unknown[])[] | undefined;
  onSuccess?: ((result: TResult, input: TInput) => void) | undefined;
}): UseMutationResult<TResult, unknown, TInput> {
  const notify = useNotify();
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TInput>({
    mutationFn: options.mutationFn,
    onSuccess: (result, input) => {
      for (const queryKey of options.invalidates ?? []) {
        void queryClient.invalidateQueries({ queryKey });
      }

      if (options.success) {
        const text =
          typeof options.success === 'function' ? options.success(result, input) : options.success;

        notify.success(text);
      }

      options.onSuccess?.(result, input);
    },

    // The raw error, not a string: `useNotify` derives the tone from it, so a plan limit arrives as
    // a boundary rather than as something red and alarming.
    onError: (error) => {
      notify.error(error);
    },
  });
}

/**
 * Runs a mutation from a form's `onFinish`, answering the question ProComponents actually asks:
 * may the drawer close?
 *
 * The rejection is swallowed rather than rethrown because {@link useApiMutation} has already shown
 * it. Letting it escape would produce an unhandled rejection *and* close the form over a failed
 * write, losing what the user typed.
 */
export async function submitVia<TResult, TInput>(
  mutation: UseMutationResult<TResult, unknown, TInput>,
  input: TInput,
): Promise<boolean> {
  try {
    await mutation.mutateAsync(input);
    return true;
  } catch {
    return false;
  }
}

/** A thrown value as one line of text, never "[object Object]". */
export function describeError(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) {
    return error.message;
  }

  return error instanceof Error ? error.message : String(error);
}
